import { FileText, MagnifyingGlass } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { Link, useRouterState } from "@tanstack/react-router";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApi } from "~/api/provider";
import { queryKeys, useCurrentWorkspace, useDocument, useDocuments } from "~/api/queries";
import type { Document } from "~/api/types";
import { useDocumentAncestors } from "~/features/documents/useDocumentAncestors";
import { Spinner } from "~/foundation/components/Spinner";
import { cn } from "~/foundation/utils/cn";
import { useShellStore } from "~/shell/store";
import { TREE_ROW_ACTIVE_CLASS, TREE_ROW_CLASS, TreeCaret } from "./CollectionTree";

export function DocumentsNav({
  pathname,
  onNavigate,
}: {
  pathname: string;
  onNavigate?: (() => void) | undefined;
}) {
  const { t } = useTranslation();
  const active = pathname === "/documents" || pathname.startsWith("/documents/");
  const collapsed = useShellStore((s) => s.collapsedSections.documentTree);
  const setCollapsed = useShellStore((s) => s.setSectionCollapsed);
  const expanded = !(collapsed ?? !active);
  const id = useId();
  useEffect(() => {
    if (pathname === "/documents" || pathname.startsWith("/documents/")) {
      setCollapsed("documentTree", false);
    }
  }, [pathname, setCollapsed]);

  return (
    <div>
      <div className={cn("flex items-center rounded-sm", active && "bg-sidebar-active-bg")}>
        <TreeCaret
          expanded={expanded}
          controls={id}
          name={t("Documents")}
          onToggle={() => setCollapsed("documentTree", expanded)}
        />
        <Link
          to="/documents"
          onClick={onNavigate}
          aria-current={pathname === "/documents" ? "page" : undefined}
          className={cn(TREE_ROW_CLASS, "font-medium")}
        >
          <FileText size={18} aria-hidden className="shrink-0" />
          <span>{t("Documents")}</span>
        </Link>
      </div>
      {expanded ? <DocumentTree id={id} pathname={pathname} onNavigate={onNavigate} /> : null}
    </div>
  );
}

function DocumentTree({
  id,
  pathname,
  onNavigate,
}: {
  id: string;
  pathname: string;
  onNavigate?: (() => void) | undefined;
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  useEffect(() => {
    const timeout = setTimeout(() => setQuery(search.trim()), 200);
    return () => clearTimeout(timeout);
  }, [search]);
  const { data, isLoading, isError, refetch } = useDocuments({
    status: "published",
    limit: 100,
    ...(query ? { query } : {}),
  });
  const routeSearch = useRouterState({ select: (s) => s.location.search }) as { ws?: string };
  const workspace = useCurrentWorkspace();
  // A source deep link may be waiting for the scene's workspace gate. Do not prefetch
  // its document against the previous workspace while that cookie switch is in flight.
  const activeId =
    !routeSearch.ws || workspace.data?.id === routeSearch.ws
      ? (/^\/documents\/([^/]+)/.exec(pathname)?.[1] ?? "")
      : "";
  const { data: current } = useDocument(activeId);
  const { data: ancestors } = useDocumentAncestors(current);
  const setExpanded = useShellStore((s) => s.setDocumentExpanded);
  useEffect(() => {
    for (const parent of ancestors ?? []) setExpanded(parent.id, true);
  }, [ancestors, setExpanded]);
  useEffect(() => {
    if (activeId) setExpanded(activeId, true);
  }, [activeId, setExpanded]);
  const documents = useMemo(() => {
    const all = new Map((data ?? []).map((doc) => [doc.id, doc]));
    if (!query) {
      for (const parent of ancestors ?? []) all.set(parent.id, parent);
      if (current) all.set(current.id, current);
    }
    return [...all.values()];
  }, [data, current, ancestors, query]);
  const roots = documents.filter((doc) => {
    if (query) return true;
    const seen = new Set([doc.id]);
    let parent = documents.find((candidate) => candidate.id === doc.parentDocumentId);
    if (!parent) return true;
    // Malformed cycles still have one reachable root and can never recurse indefinitely.
    while (parent) {
      if (seen.has(parent.id)) return doc.id === [...seen].sort()[0];
      seen.add(parent.id);
      parent = documents.find((candidate) => candidate.id === parent?.parentDocumentId);
    }
    return false;
  });

  return (
    <div id={id} className="ms-3 mt-1 border-s border-divider ps-1">
      <label className="mx-1 mb-1 flex h-7 items-center gap-1.5 rounded-sm border border-divider bg-bg px-2 text-text-tertiary">
        <MagnifyingGlass size={13} aria-hidden className="shrink-0" />
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          aria-label={t("Find a document")}
          placeholder={t("Find a document")}
          className="min-w-0 flex-1 bg-transparent text-small text-text outline-none placeholder:text-text-tertiary"
        />
      </label>
      <div className="max-h-[40vh] overflow-y-auto overscroll-contain scrollbar-thin">
        {isLoading ? (
          <div className="p-2">
            <Spinner size={14} label={t("Loading")} />
          </div>
        ) : isError ? (
          <button type="button" onClick={() => void refetch()} className="p-2 text-small text-link">
            {t("Retry")}
          </button>
        ) : roots.length ? (
          <ul className="flex flex-col gap-0.5">
            {roots.map((doc) => (
              <DocumentNode
                key={doc.id}
                document={doc}
                documents={documents}
                activeId={activeId}
                path={[]}
                flat={Boolean(query)}
                onNavigate={onNavigate}
              />
            ))}
          </ul>
        ) : (
          <p className="px-2 py-3 text-small text-text-tertiary">{t("No documents")}</p>
        )}
      </div>
      {data && data.length >= 100 ? (
        <p className="px-2 py-1 text-xs text-text-tertiary">
          {t("Showing recent documents. Search to find more.")}
        </p>
      ) : null}
    </div>
  );
}

function DocumentNode({
  document,
  documents,
  activeId,
  path,
  flat,
  onNavigate,
}: {
  document: Document;
  documents: Document[];
  activeId: string;
  path: string[];
  flat: boolean;
  onNavigate: (() => void) | undefined;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const id = useId();
  const expanded = useShellStore((s) => s.expandedDocuments[document.id] === true);
  const setExpanded = useShellStore((s) => s.setDocumentExpanded);
  const query = { status: "published" as const, parentDocumentId: document.id, limit: 100 };
  const childrenQuery = useQuery({
    queryKey: queryKeys.documents(query),
    queryFn: () => api.documents.list(query),
    enabled: expanded && !flat,
  });
  const children = [
    ...new Map(
      [
        ...documents.filter((doc) => doc.parentDocumentId === document.id),
        ...(childrenQuery.data ?? []),
      ].map((doc) => [doc.id, doc]),
    ).values(),
  ].filter((doc) => doc.id !== document.id && !path.includes(doc.id));
  const hasChildren = !flat && (children.length > 0 || !childrenQuery.isSuccess);
  const active = document.id === activeId;
  const linkRef = useRef<HTMLAnchorElement>(null);
  useEffect(() => {
    if (active && linkRef.current?.offsetParent) {
      linkRef.current.scrollIntoView?.({ block: "nearest" });
    }
  }, [active]);
  return (
    <li>
      <div className="flex items-center gap-0.5">
        <TreeCaret
          expanded={expanded && hasChildren}
          controls={id}
          name={document.title}
          onToggle={hasChildren ? () => setExpanded(document.id, !expanded) : null}
        />
        <Link
          to="/documents/$documentId"
          params={{ documentId: document.id }}
          ref={linkRef}
          onClick={onNavigate}
          aria-current={active ? "page" : undefined}
          title={document.title}
          className={cn(TREE_ROW_CLASS, "text-small", active && TREE_ROW_ACTIVE_CLASS)}
        >
          {document.emoji ? (
            <span aria-hidden className="shrink-0">
              {document.emoji}
            </span>
          ) : (
            <FileText size={14} aria-hidden className="shrink-0 text-text-tertiary" />
          )}
          <span className="truncate">{document.title}</span>
        </Link>
      </div>
      {expanded && hasChildren ? (
        <ul id={id} className="ms-3 border-s border-divider ps-1">
          {childrenQuery.isLoading ? (
            <li className="p-2">
              <Spinner size={12} label={t("Loading")} />
            </li>
          ) : null}
          {childrenQuery.isError ? (
            <li>
              <button
                type="button"
                onClick={() => void childrenQuery.refetch()}
                className="p-2 text-small text-link"
              >
                {t("Retry")}
              </button>
            </li>
          ) : null}
          {children.map((child) => (
            <DocumentNode
              key={child.id}
              document={child}
              documents={documents}
              activeId={activeId}
              path={[...path, document.id]}
              flat={false}
              onNavigate={onNavigate}
            />
          ))}
          {(childrenQuery.data?.length ?? 0) >= 100 ? (
            <li className="px-2 py-1 text-xs text-text-tertiary">
              {t("Showing recent documents. Search to find more.")}
            </li>
          ) : null}
        </ul>
      ) : null}
    </li>
  );
}
